import { ReactNode } from 'react';
import { CalendarPricesResponseDTO } from '../../DTO/calendarResponseDTO';
import { DateUtils } from '../../helpers/dateUtils';
import { toTitleString } from '../../helpers/utils';
import { Format } from '../../helpers/formatters';

const formatShortWeekday = (_: unknown, date: Date): string => {
    const fullWeekdays = ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'];
    return fullWeekdays[date.getDay()];
};

const calcTotalAvailable = <T extends keyof CalendarPricesResponseDTO>(
    item: CalendarPricesResponseDTO[T]
): number => {
    if (item.schedules.length === 0) return 0;

    return item.schedules?.reduce((a, b) => ({
        ...a,
        available: a.available + b.available,
    })).available;
};

const getTitleContent =
    (prices?: CalendarPricesResponseDTO, ignoreBasicValidation?: boolean, forcedPrice?: number) =>
    ({ date, view }: { date: Date; view: string }) => {
        if (view !== 'month') return;
        if (!prices) return [];
        const children: ReactNode[] = [];

        const dateKey = DateUtils.formatToInitalYear(date);
        const datePriceItem = prices[dateKey];
        if (ignoreBasicValidation) {
            children.push(<div className="calendar-date-tag green" />);
            
            return children;
        }

        if (!datePriceItem) return children;

        const totalAvailable = calcTotalAvailable(datePriceItem);

        if (totalAvailable === 0) {
            children.push(<div className="calendar-date-tag red" />);
        } else if (totalAvailable <= 5) {
            children.push(<div className="calendar-date-tag orange" />);
        } else {
            children.push(<div className="calendar-date-tag green" />);
        }

        children.push(
            <span className="price" style={{ opacity: 0.7 }}>
                {Format.currencyWithoutFraction(forcedPrice ?? datePriceItem.value)}
            </span>
        );

        return children;
    };

const formatMonthYear = (locale: Intl.LocalesArgument, date: Date) => {
    const month = date.toLocaleString(locale, { month: 'long' });
    const year = date.getFullYear();
    return `${toTitleString(month)} ${year}`;
};

const getTileClassName =
    (prices?: CalendarPricesResponseDTO) =>
    ({ date }: { date: Date; view: string }) => {
        if (!prices) return null;
        const dateKey = DateUtils.formatToInitalYear(date);
        const datePriceItem = prices[dateKey];
        if (!datePriceItem) return null;
        const totalAvailable = calcTotalAvailable(datePriceItem);
        if (totalAvailable === 0) {
            return 'disabled';
        }

        return null;
    };

const calendarUtils = {
    formatShortWeekday,
    formatMonthYear,
    getTitleContent,
    getTileClassName,
};

export default calendarUtils;
